import {useMutation} from '@tanstack/react-query';
import {type as arkType} from 'arktype';
import {VerificationMethodType} from '~/features/two-factor';
import {expectEither, HttpClient, POST, ServerValidationResult, usePublicHttpClient} from '~/library/network-facade';

/*------------------------v1/forgot-password/start------------------------*/

export type ForgotPasswordStartRequest = {
    username: string;
    repeated_event: 'yes' | 'no';
    verificationMethod?: VerificationMethodType;
    captchaToken: string;
};

export const ForgotPasswordStartErrorCode = {
    requestLimit: 'request_limit',
    cooldownPeriod: 'cooldown_period',
    notFound: 'not-found',
} as const;

export function forgotPasswordStart(http: HttpClient, data: ForgotPasswordStartRequest) {
    return http('v1/forgot-password/start', POST(data)).then(
        ...expectEither({
            200: arkType({
                code: '0',
                retry_at: 'number',
                retry_in: 'number',
                tenant_uuid: 'string',
            }).or({
                code: '1',
                retry_at: 'number',
                retry_in: 'number',
                tenant_uuid: 'null',
            }),
            404: null,
            429: arkType({
                error_code: arkType.enumerated(
                    ForgotPasswordStartErrorCode.requestLimit,
                    ForgotPasswordStartErrorCode.cooldownPeriod
                ),
                retry_in: 'number',
            }),
        })
    );
}

export function useForgotPasswordStartMutation() {
    const http = usePublicHttpClient();
    return useMutation({
        mutationKey: ['v1/forgot-password/start'],
        mutationFn: async (data: ForgotPasswordStartRequest) => {
            const res = await forgotPasswordStart(http, data);
            switch (res.status) {
                case 200: {
                    return {value: res.json};
                }
                case 429: {
                    return {problem: res.json};
                }
                case 404: {
                    return {
                        problem: {
                            error_code: ForgotPasswordStartErrorCode.notFound,
                        },
                    };
                }
                case 'Abort': {
                    throw res.reason;
                }
            }
        },
    });
}

/*------------------------v1/forgot-password/verify-code------------------------*/

export const ForgotPasswordVerifyCodeErrorCode = {
    codeExpired: 'code_expired',
    wrongCode: 'wrong_code',
    notFound: 'not_found',
    validationFailed: 'validation_failed',
    limitReached: 'code_verification_limit_reached',
} as const;

export type VerifyCodeRequest = {
    username: string;
    code: string;
};
export function verifyCode(http: HttpClient, data: VerifyCodeRequest) {
    return http('v1/forgot-password/verify-code', POST(data)).then(
        ...expectEither({
            200: arkType({
                uuid: 'string',
            }),
            404: null,
            400: arkType({
                '+': 'reject',
                'code': 'number',
                'message': 'string',
                'error_code': arkType.enumerated(
                    ForgotPasswordVerifyCodeErrorCode.codeExpired,
                    ForgotPasswordVerifyCodeErrorCode.wrongCode
                ),
            }).or(
                ServerValidationResult.pipe(
                    arkType({
                        code: 'string?',
                    })
                )
            ),
            429: arkType({
                error_code: arkType.enumerated(ForgotPasswordVerifyCodeErrorCode.limitReached),
                retry_in: 'number',
            }),
        })
    );
}

export function useForgotPasswordVerifyCodeMutation() {
    const http = usePublicHttpClient();
    return useMutation({
        mutationKey: ['v1/forgot-password/verify-code'],
        mutationFn: async (data: VerifyCodeRequest) => {
            const res = await verifyCode(http, data);

            switch (res.status) {
                case 'Abort': {
                    throw res.reason;
                }
                case 200: {
                    return {value: res.json.uuid};
                }
                case 404: {
                    return {
                        problem: {
                            error_code: ForgotPasswordVerifyCodeErrorCode.notFound,
                        },
                    };
                }
                case 400: {
                    if ('error_code' in res.json) {
                        return {problem: res.json};
                    }
                    return {
                        problem: {
                            issues: res.json,
                            error_code: ForgotPasswordVerifyCodeErrorCode.validationFailed,
                        },
                    };
                }
                case 429: {
                    return {problem: res.json};
                }
            }
        },
    });
}

/*------------------------v1/forgot-password/finish------------------------*/

export type ForgotPasswordFinishRequest = {
    username: string;
    uuid: string;
    password: string;
};

export const ForgotPasswordFinishErrorCode = {
    validationFailed: 'validation_failed',
    wrongCode: 'wrong_code',
    codeExpired: 'code_expired',
    somethingWrong: 'something_wrong',
} as const;

export function forgotPasswordFinish(http: HttpClient, data: ForgotPasswordFinishRequest) {
    return http('v1/forgot-password/finish', POST(data)).then(
        ...expectEither({
            200: null,
            400: arkType({
                '+': 'reject',
                'code': 'number',
                'message': 'string',
                'error_code': arkType.enumerated(
                    ForgotPasswordFinishErrorCode.wrongCode,
                    ForgotPasswordFinishErrorCode.codeExpired,
                    ForgotPasswordFinishErrorCode.somethingWrong
                ),
            }).or(
                ServerValidationResult.pipe(
                    arkType({
                        username: 'string?',
                        uuid: 'string?',
                        password: 'string?',
                    })
                )
            ),
        })
    );
}

export function useForgotPasswordFinishMutation() {
    const http = usePublicHttpClient();
    return useMutation({
        mutationKey: ['v1/forgot-password/finish'],
        mutationFn: async (data: ForgotPasswordFinishRequest) => {
            const res = await forgotPasswordFinish(http, data);
            switch (res.status) {
                case 200: {
                    return {ok: true};
                }
                case 400: {
                    if ('error_code' in res.json) {
                        return {problem: res.json};
                    }
                    return {
                        problem: {
                            error_code: ForgotPasswordFinishErrorCode.validationFailed,
                            issues: res.json,
                        },
                    };
                }
                case 'Abort': {
                    throw res.reason;
                }
            }
        },
    });
}
